// Pages through ALL rows of a Supabase query, bypassing PostgREST's max-rows
// cap (1000). A single `.limit(10000)` (or no limit at all) does NOT lift that
// cap — PostgREST silently returns only the first 1000 rows in the requested
// order, hiding everything after them. The only reliable way to read the full
// set is to fetch successive 1000-row windows with `.range(from, to)` (each
// window stays within the cap) until a short page signals the end.
//
// Pass a function that applies `.range(from, to)` to your already-built query
// (preserving its `.select()`, filters, and `.order()`).
export async function fetchAllRows<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: unknown }>,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await page(from, from + pageSize - 1);
    if (error) {
      throw error;
    }
    const batch = data ?? [];
    all.push(...batch);
    if (batch.length < pageSize) {
      break;
    }
  }
  return all;
}

/**
 * One window of a name-sorted read in a TOTAL order: name, then id as the
 * tiebreaker, so rows that share a name can never shift between page windows
 * (no row skipped or repeated across pages). Use as the `page` callback of
 * fetchAllRows for any name-sorted picker source (lists/segments etc.):
 *
 *   fetchAllRows((from, to) =>
 *     pageByNameThenId(supabase.from("lists").select("id, name"), from, to))
 */
export function pageByNameThenId<
  Q extends {
    order: (column: string, options?: { ascending?: boolean }) => Q;
    range: (from: number, to: number) => R;
  },
  R,
>(query: Q, from: number, to: number): R {
  return query
    .order("name", { ascending: true })
    .order("id", { ascending: true })
    .range(from, to);
}
